import { createSelector } from 'reselect';
import { currentAccountConsumerIdSelector } from 'store/account/selectors';
import { RootState } from 'store/types';
import { testResultsListDataSelectors } from './reducers.slice';
import { LabDetails, TestResult, TestResultCategory, TestResultsList } from './types';
import { getDetailsPath } from './utils';

/**
 * Imaging Selectors
 */
export * from './imaging/selectors';

/**
 * Lab Selectors
 */
export * from './lab/selectors';

/**
 * Microbiology Selectors
 */
export * from './microbiology/selectors';

/**
 * Pathology Selectors
 */
export * from './pathology/selectors';

/**
 * Test Result Details
 */
export const testResultDetailsSelector = (state: RootState) => state.testResults.testResultDetails;

export const testResultDetailsLoadingSelector = createSelector(
  testResultDetailsSelector,
  testResultDetails => testResultDetails.isFetching
);

export const testResultDetailsErrorSelector = createSelector(
  testResultDetailsSelector,
  testResultDetails => testResultDetails.error
);

export const testResultDetailsDataSelector = createSelector(
  testResultDetailsSelector,
  testResultDetails => {
    if (!testResultDetails?.data) {
      return null;
    }

    const retData = { ...(testResultDetails.data || {}) };
    if (
      Array.isArray(retData?.content) &&
      retData.content.find(contentRec => contentRec.category === TestResultCategory.LAB)
    ) {
      // This is an expansion response. Mold it into the dfd shape that we expect.
      const customInfoContentRec = retData.content.find(
        contentRec => !!contentRec.customInformation
      );

      retData.content = retData.content.map(contentRec => {
        const tmp = {
          ...contentRec
        };
        if (
          tmp.category === TestResultCategory.LAB &&
          !(tmp.componentEventIds || []).length &&
          !!(tmp.components || []).length
        ) {
          tmp.componentEventIds = tmp.components.map(comp => comp.eventId.replace(/^M/, ''));
          if (tmp.eventId && !tmp.componentEventIds.includes(tmp.eventId)) {
            tmp.componentEventIds.push(tmp.eventId);
          }
        }

        return {
          ...tmp
        };
      });

      retData.content = {
        errors: [],
        results: [...retData.content],
        total: retData.content.length
      } as LabDetails;

      if (customInfoContentRec?.customInformation) {
        retData.content.customInformation = {
          ...customInfoContentRec.customInformation
        };
      }
    }

    return retData;
  }
);

export const labTestResultDataSelector = createSelector(
  testResultDetailsDataSelector,
  testResultDetailsData => (testResultDetailsData?.content as LabDetails)?.results || []
);

// Test Results list

export const testResultsListSelector = (state: RootState) => state.testResults.testResultsList;

export const testResultsListDataSelector = createSelector(
  [currentAccountConsumerIdSelector, state => state],
  (consumerId, state) => {
    const testResultsList = testResultsListDataSelectors.selectById(state, consumerId);
    return (testResultsList?.data as TestResultsList)?.results
      ? (testResultsList.data as TestResultsList).results
      : (testResultsList?.data as TestResult[]) || [];
  }
);

export const testResultsListSliceRequestStateSelector = createSelector(
  [currentAccountConsumerIdSelector, state => state],
  (consumerId, state) => {
    const results = testResultsListDataSelectors.selectById(state, consumerId);
    return results?.requestState;
  }
);

export const testResultsListRequestStateSelector = createSelector(
  [testResultsListSliceRequestStateSelector, testResultsListSelector],
  (sliceRequestState, list) => {
    return sliceRequestState || list.requestState;
  }
);
export const testResultsListIsFetchingSelector = createSelector(
  testResultsListSelector,
  testResultsList => !!testResultsList?.isFetching
);
export const testResultsListErrorSelector = createSelector(
  testResultsListSelector,
  testResultsList => testResultsList?.error
);

export const testResultsListLastUpdatedSelector = createSelector(
  [currentAccountConsumerIdSelector, state => state],
  (consumerId, state) => {
    const results = testResultsListDataSelectors.selectById(state, consumerId);
    return results?.cacheUpdatedAt || '';
  }
);

export const testResultsListSortSelector = createSelector(
  testResultsListSelector,
  testResultsList => testResultsList?.sortBy
);

export const testResultsListFiltersSelector = createSelector(
  testResultsListSelector,
  testResultsList => testResultsList?.filters
);

export const testResultsListFilterOptionsSelector = createSelector(
  testResultsListSelector,
  testResultsList => testResultsList?.filterOptions
);

export const testResultListFilterDateRangeSelector = createSelector(
  testResultsListSelector,
  testResultsList => ({
    year: testResultsList?.year,
    startDate: testResultsList?.startDate,
    endDate: testResultsList?.endDate
  })
);

export const testResultListByEncounterSelector = (state: RootState) =>
  state.testResults.testResultListByEncounter;

export const testResultListByEncounterDataSelector = createSelector(
  testResultListByEncounterSelector,
  testResultList => testResultList?.data || []
);
export const testResultListByEncounterIsFetchingSelector = createSelector(
  testResultListByEncounterSelector,
  testResultList => !!testResultList?.isFetching
);
export const testResultListByEncounterErrorSelector = createSelector(
  testResultListByEncounterSelector,
  testResultList => testResultList?.error
);

export const testWithDetailsDataSelector = (state: RootState) =>
  state.testResults.testResultWithDetails.data;
export const testWithDetailsisFetchingSelector = (state: RootState) =>
  state.testResults.testResultWithDetails.isFetching;
export const testWithDetailsErrorSelector = (state: RootState) =>
  state.testResults.testResultWithDetails.error;

export const searchFormattedTestResults = createSelector(testResultsListDataSelector, data => {
  return data.map(item => {
    return {
      title: item.name,
      provider: item.providerNames,
      location: item.providerLocation,
      date: item.testDate,
      icon: 'TestResultsIcon',
      keywords: [item.category],
      navWebPath: getDetailsPath(item),
      navParams: { state: { result: item } },
      category: 'TestResults'
    };
  });
});
